import { ISignalRService } from './signalR.types';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';

export interface Event<T> {
  data: T;
}

export type SubscriptionCallback<T> = (event: Event<T>) => void;

export type SubscriptionMatcher<T> = (event: Event<T>) => boolean;

export type SubscriptionRecord<T, K extends string | number | symbol> = {
  subscriptionID: string;
  callback?(event: Event<T>): void;
  matcher?(event: Event<T>): boolean;
  isPaused?: boolean;
} & Record<K, string[]>;

export type SubscriptionResponse<T, K extends string | number | symbol> = {
  setCallback(callback: SubscriptionCallback<T>): void;
  setMatcher(matcher: SubscriptionMatcher<T>): void;
  pause(): void;
  resume(): void;
  stream(requests: Record<K, string[]>): void;
  unsubscribe(): void;
};

export class SignalREventCentre<T, K extends Record<string, string[]>> {
  private subscriptions: Map<string, SubscriptionRecord<T, keyof K>> = new Map();
  private requestKeys: (keyof K)[];
  private signalR: ISignalRService;
  private signalRAttributeName: string;
  private aggregatedRequest: Record<keyof K, string[]> = {} as Record<keyof K, string[]>;

  protected constructor(signalR: ISignalRService, signalRAttributeName: string, requestKeys: (keyof K)[]) {
    this.signalR = signalR;
    this.signalRAttributeName = signalRAttributeName;
    this.requestKeys = requestKeys;
    signalR.connection.on(signalRAttributeName, (update) => {
      const event: Event<T> = {
        data: update
      };
      this.publish(event);
    });
  }

  private publish(event: Event<T>) {
    Array.from(this.subscriptions.values()).forEach((s) => {
      if (s.callback && s.isPaused !== true && (!s.matcher || s.matcher(event))) {
        s.callback(event);
      }
    });
  }

  public subscribe(): SubscriptionResponse<T, keyof K> {
    const subscriptionID = uuidv4();
    const subscriptionRecord: SubscriptionRecord<T, keyof K> = {
      subscriptionID,
      ...this.requestKeys.reduce((acc, key) => ({ ...acc, [key]: [] }), {} as Record<keyof K, string[]>)
    };
    this.subscriptions.set(subscriptionID, subscriptionRecord);

    return {
      setCallback: (callback) => {
        const subscription = this.subscriptions.get(subscriptionID);
        if (subscription === undefined) {
          return;
        }
        subscription.callback = callback;
      },
      setMatcher: (matcher) => {
        const subscription = this.subscriptions.get(subscriptionID);
        if (subscription === undefined) {
          return;
        }
        subscription.matcher = matcher;
      },
      pause: () => {
        const subscription = this.subscriptions.get(subscriptionID);
        if (subscription === undefined) {
          return;
        }
        subscription.isPaused = true;
      },
      resume: () => {
        const subscription = this.subscriptions.get(subscriptionID);
        if (subscription === undefined) {
          return;
        }
        subscription.isPaused = false;
      },
      stream: (requests) => {
        Object.keys(requests).forEach((key) => {
          const typedKey = key as keyof K;
          const typedValue = requests[typedKey] as SubscriptionRecord<T, keyof K>[keyof K];
          subscriptionRecord[typedKey] = typedValue;
        });
        this.updateFromStream();
      },
      unsubscribe: () => {
        this.subscriptions.delete(subscriptionID);
      }
    };
  }

  private updateFromStream() {
    const newAggRequest = this.requestKeys.reduce((acc, key) => {
      const values = [
        ...new Set(
          Array.from(this.subscriptions.values())
            .map((request) => request[key])
            .flat()
        )
      ];
      return {
        ...acc,
        [key]: values
      };
    }, {} as Record<keyof K, string[]>);

    if (_.isEqual(this.aggregatedRequest, newAggRequest)) {
      return;
    }

    this.signalR.update(this.signalRAttributeName, newAggRequest);
    this.aggregatedRequest = newAggRequest;
  }
}
